import { Link } from "react-router-dom";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { ArrowUpRight } from "lucide-react";

// External destination for the story submission form.
const TALLY_URL = `https://tally.so/r/${process.env.REACT_APP_TALLY_ID}`;

export default function ShareYourStory() {
  return (
    <div className="pt-10 md:pt-16" data-testid="share-your-story-page">
      <section className="editorial-container">
        <SectionLabel number="No. 006" label="Share Your Story" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7vw] lg:text-[6rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="Share your" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="story." as="span" className="block italic text-terracotta" delay={0.35} animate />
        </h1>
      </section>

      <section className="mt-16 md:mt-24 editorial-container">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
            <FadeUp>
              <div className="space-y-8 font-serif text-xl md:text-2xl leading-[1.5] text-ink/85">
                <p>
                  There are so many parts of motherhood we don't speak about openly enough.
                </p>
                <p>
                  The loneliness. The excitement. The guilt. The identity crisis. The difficult
                  in-laws. The new family. The breastfeeding struggles. The first steps. The career
                  sacrifices. The joy. The grief. The moments that changed us forever.
                </p>
                <p>
                  We carry these stories quietly, often believing we're the only ones who have lived
                  them.
                </p>
                <p className="italic">The Safe Mama Editorial wants to change that.</p>
                <p>
                  Every month, we send a beautifully written anonymous letter based on the real
                  experience of a mother — something another mum might read and think,{" "}
                  <em className="italic">"I'm not the only one."</em>
                </p>
                <p>
                  Your experience doesn't have to be dramatic or extraordinary to belong here. It
                  just has to be real.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="mt-20 md:mt-24 border-t border-ink/15 pt-10">
                <div className="overline text-ink/60">A few things to know before you begin</div>
                <ul className="mt-8 space-y-5 font-serif text-lg md:text-xl leading-[1.55] text-ink/80 max-w-2xl">
                  <li>
                    You do not need to share your name, email address, phone number, or any
                    identifying information.
                  </li>
                  <li>Share as much or as little as you feel comfortable sharing.</li>
                  <li>You can skip any question.</li>
                  <li>If your story is selected, it may be edited and rewritten as an anonymous letter.</li>
                  <li>Details that could identify you will be removed or changed.</li>
                  <li>Your story will never be published with your identity attached.</li>
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="mt-20 md:mt-24">
                <a
                  href={TALLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill"
                  data-testid="share-story-cta"
                >
                  Share your story <ArrowUpRight size={14} />
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="mt-24 md:mt-28 border-t border-ink/15 pt-10 font-serif text-xl md:text-2xl leading-[1.5] text-ink/85 italic">
                <p>
                  If your story helps even one mother feel seen, understood, or a little less alone,
                  then together we've created something worthwhile.
                </p>
                <p className="mt-6 not-italic">Thank you for trusting us with it.</p>
              </div>

              <div className="mt-14">
                <div className="overline text-ink/50">With love,</div>
                <div className="mt-4 font-script text-5xl text-terracotta">Ikshiita</div>
                <div className="mt-3 font-serif italic text-lg text-ink/70">Founder, Editor, Fellow Mama</div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
